import { shortId, TicketStatusBadge } from "@lumberset/support-ui";
import {
  ago,
  correctionReasons,
  date,
  dateTime,
  money,
  ReviewMode,
  ReviewStatus,
} from "@lumberset/takeoff-ui";
import {
  Container,
  EmptyState,
  ProgressBar,
  SectionHeader,
  Stat,
  StatusBadge,
  Table,
  type Tone,
} from "@lumberset/ui";
import { Lifebuoy, ListCheckbox } from "@medusajs/icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Crumbs } from "../../components/crumbs";
import { type MyWork, myWorkQuery } from "../../lib/queries-team";

/** What the signed-in Staff member finished, and what is on their desk. */
export const Route = createFileRoute("/_app/my-work")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(myWorkQuery),
  component: MyWorkScreen,
});

type Numbers = MyWork["numbers"]["week"];

const minutes = (value: number | null) => (value === null ? "-" : String(value));

const tiles: { label: string; value: (numbers: Numbers) => string | number }[] = [
  { label: "Reviews finished", value: (numbers) => numbers.reviewsFinished },
  { label: "Minutes per review", value: (numbers) => minutes(numbers.minutesPerReview) },
  { label: "Corrections", value: (numbers) => numbers.corrections },
  { label: "Tickets resolved", value: (numbers) => numbers.ticketsResolved },
  { label: "Ticket replies", value: (numbers) => numbers.ticketReplies },
  { label: "Help articles", value: (numbers) => numbers.articlesWritten },
  { label: "Price proposals", value: (numbers) => numbers.priceProposals },
  { label: "Reading lessons", value: (numbers) => numbers.lessonsWritten },
];

function MyWorkScreen() {
  const { data } = useSuspenseQuery(myWorkQuery);
  const { numbers, openWork } = data;

  return (
    <>
      <Crumbs trail={[{ label: "My work" }]} />

      <Container className="divide-y overflow-hidden p-0">
        <SectionHeader
          level="h1"
          title="My work"
          description="What you finished this week, with the last 30 days under each number."
        />
        <div className="txt-compact-small flex flex-wrap items-center gap-x-4 gap-y-1 px-6 py-3 text-ui-fg-subtle">
          <span>On your desk:</span>
          <Link to="/" className="text-ui-fg-interactive hover:text-ui-fg-interactive-hover">
            {openWork.reviews} {openWork.reviews === 1 ? "review" : "reviews"}
          </Link>
          <Link to="/support" className="text-ui-fg-interactive hover:text-ui-fg-interactive-hover">
            {openWork.tickets} open {openWork.tickets === 1 ? "ticket" : "tickets"}
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-px bg-ui-border-base lg:grid-cols-4">
          {tiles.map((tile) => (
            <Stat
              key={tile.label}
              className="bg-ui-bg-base"
              label={tile.label}
              value={tile.value(numbers.week)}
              hint={`Last 30 days: ${tile.value(numbers.month)}`}
            />
          ))}
        </div>
      </Container>

      <MyReviews reviews={data.reviews} />
      <MyTickets tickets={data.tickets} />
      <MyCorrections mix={data.corrections.month} />
      <MyWriting data={data} />
    </>
  );
}

function MyReviews({ reviews }: { reviews: MyWork["reviews"] }) {
  const navigate = useNavigate();
  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title="My reviews"
        description="Reviews on your desk, and the ones you finished in the last 90 days."
      />
      {reviews.length === 0 ? (
        <EmptyState
          icon={<ListCheckbox />}
          title="No reviews yet"
          description="Claim a takeoff from the review queue and it shows here."
        />
      ) : (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Project</Table.HeaderCell>
              <Table.HeaderCell>Mode</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell className="text-right">Minutes</Table.HeaderCell>
              <Table.HeaderCell className="text-right">Corrections</Table.HeaderCell>
              <Table.HeaderCell>Approved</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {reviews.map((review) => (
              <Table.Row
                key={review.id}
                data-clickable="true"
                onClick={() => navigate({ to: "/reviews/$id", params: { id: review.id } })}
              >
                <Table.Cell className="py-2.5">
                  <div className="txt-compact-small-plus whitespace-nowrap text-ui-fg-base">
                    {review.projectName}
                  </div>
                  <div className="txt-compact-xsmall whitespace-nowrap text-ui-fg-muted">
                    {review.workspaceName}
                  </div>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <ReviewMode mode={review.mode} status={review.status} />
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <ReviewStatus status={review.status} />
                </Table.Cell>
                <Table.Cell className="tabular text-right">{minutes(review.minutes)}</Table.Cell>
                <Table.Cell className="tabular text-right">{review.corrections}</Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  {review.approvedAt ? dateTime(review.approvedAt) : "-"}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Container>
  );
}

function MyTickets({ tickets }: { tickets: MyWork["tickets"] }) {
  const navigate = useNavigate();
  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title="My tickets"
        description="Tickets assigned to you or that you answered, newest activity first."
      />
      {tickets.length === 0 ? (
        <EmptyState
          icon={<Lifebuoy />}
          title="No tickets yet"
          description="Tickets you are assigned or reply to show here."
        />
      ) : (
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Ticket</Table.HeaderCell>
              <Table.HeaderCell>From</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>My part</Table.HeaderCell>
              <Table.HeaderCell>Last activity</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {tickets.map((ticket) => (
              <Table.Row
                key={ticket.id}
                data-clickable="true"
                onClick={() => navigate({ to: "/support/tickets/$id", params: { id: ticket.id } })}
              >
                <Table.Cell className="py-2.5">
                  <div className="txt-compact-small-plus max-w-[320px] truncate text-ui-fg-base">
                    {ticket.title}
                  </div>
                  <div className="txt-compact-xsmall text-ui-fg-muted">#{shortId(ticket.id)}</div>
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  {ticket.workspaceName ?? ticket.contactName ?? (
                    <span className="text-ui-fg-muted">Staff</span>
                  )}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  <TicketStatusBadge status={ticket.status} />
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">
                  {[
                    ticket.assignedToThem ? "Assigned" : null,
                    ticket.replies > 0
                      ? `${ticket.replies} ${ticket.replies === 1 ? "reply" : "replies"}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(", ") || "-"}
                </Table.Cell>
                <Table.Cell className="whitespace-nowrap">{ago(ticket.lastActivityAt)}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Container>
  );
}

const reasonLabels = Object.fromEntries(
  correctionReasons("reviewer").map((reason) => [reason.value, reason.label]),
) as Record<string, string>;

function MyCorrections({ mix }: { mix: MyWork["corrections"]["month"] }) {
  const most = Math.max(0, ...mix.byReason.map((row) => row.count));
  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title="My corrections by reason"
        description={`${mix.total} ${mix.total === 1 ? "correction" : "corrections"} in the last 30 days. The reasons show where Reading goes wrong.`}
      />
      {mix.byReason.length === 0 ? (
        <EmptyState
          className="py-10"
          title="No corrections in the last 30 days"
          description="Changes you record while reviewing are counted here."
        />
      ) : (
        <div className="flex flex-col gap-y-3 px-6 py-4">
          {mix.byReason.map((row) => (
            <div key={row.reason} className="flex flex-col gap-y-1">
              <div className="txt-compact-small flex items-center justify-between gap-x-4">
                <span className="min-w-0 truncate text-ui-fg-base">
                  {reasonLabels[row.reason] ?? row.reason}
                </span>
                <span className="tabular text-ui-fg-subtle">{row.count}</span>
              </div>
              <ProgressBar value={most === 0 ? 0 : (row.count / most) * 100} />
            </div>
          ))}
        </div>
      )}
    </Container>
  );
}

const decisionLooks: Record<string, { label: string; tone: Tone }> = {
  pending: { label: "Waiting", tone: "orange" },
  approved: { label: "Approved", tone: "green" },
  rejected: { label: "Rejected", tone: "red" },
  withdrawn: { label: "Withdrawn", tone: "grey" },
  draft: { label: "Draft", tone: "grey" },
  published: { label: "Published", tone: "green" },
  retired: { label: "Retired", tone: "grey" },
};

function Decision({ status }: { status: string }) {
  const look = decisionLooks[status] ?? { label: status, tone: "grey" as const };
  return <StatusBadge color={look.tone}>{look.label}</StatusBadge>;
}

/** Articles, price proposals and lessons, only when there are some. */
function MyWriting({ data }: { data: MyWork }) {
  const { articles, proposals, lessons } = data;
  if (articles.length + proposals.length + lessons.length === 0) return null;
  return (
    <Container className="divide-y p-0">
      <SectionHeader
        title="Articles, prices and lessons"
        description="Help articles you started, prices you proposed and reading lessons you wrote."
      />
      <ul className="divide-y divide-ui-border-base">
        {articles.map((article) => (
          <li key={article.id}>
            <Link
              to="/help/articles/$id"
              params={{ id: article.id }}
              className="flex items-center justify-between gap-x-4 px-6 py-3 hover:bg-ui-bg-base-hover"
            >
              <span className="min-w-0">
                <span className="txt-compact-small-plus block truncate text-ui-fg-base">
                  {article.title}
                </span>
                <span className="txt-compact-xsmall block text-ui-fg-muted">
                  Help article, updated {ago(article.updatedAt)}
                </span>
              </span>
              <Decision status={article.status} />
            </Link>
          </li>
        ))}
        {proposals.map((proposal) => (
          <li key={proposal.id} className="flex items-center justify-between gap-x-4 px-6 py-3">
            <span className="min-w-0">
              <span className="txt-compact-small-plus block truncate text-ui-fg-base">
                {proposal.description}
              </span>
              <span className="txt-compact-xsmall block text-ui-fg-muted">
                Price {money(proposal.priceCents)}, proposed {date(proposal.createdAt)}
              </span>
            </span>
            <Decision status={proposal.status} />
          </li>
        ))}
        {lessons.map((lesson) => (
          <li key={lesson.id} className="flex items-start justify-between gap-x-4 px-6 py-3">
            <span className="min-w-0">
              <span className="txt-small block text-pretty text-ui-fg-base">{lesson.text}</span>
              <span className="txt-compact-xsmall block text-ui-fg-muted">
                Reading lesson, written {date(lesson.createdAt)}
              </span>
            </span>
            <Decision status={lesson.status} />
          </li>
        ))}
      </ul>
    </Container>
  );
}
